import { createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import { CargoScanReading, cargoScanReadingsT } from "dora-contracts";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";

const prefix = "data/scan-readings";

type State = {
  entities: Record<string, CargoScanReading[] | undefined>;
  requestStatus: Record<string, "loading" | "loaded" | "error" | undefined>;
};

const initialState: State = {
  entities: {},
  requestStatus: {},
};

/**
 * The AI readings of the documents uploaded on one cargo. Unlike the source
 * mail there is no "not found" case - a cargo with no readings answers with an
 * empty list - so every failure here is a real one and is reported: an empty
 * card and a failed card are indistinguishable to the dispatcher otherwise,
 * and they would go on to invoice believing nothing was read off the slips.
 */
export const loadScanReadings = createErrorReportingAsyncThunk(
  `${prefix}/load-scan-readings`,
  async (cargoId: string) => {
    const readings = await axios
      .get(`/api/scan-readings/cargo/${cargoId}`)
      .then(getData)
      .then(tPromise.decode(cargoScanReadingsT));
    return { cargoId, readings };
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    unloadScanReadings: (state, action: { payload: string }) => {
      delete state.entities[action.payload];
      delete state.requestStatus[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadScanReadings.pending, (state, action) => {
      state.requestStatus[action.meta.arg] = "loading";
    });
    builder.addCase(loadScanReadings.rejected, (state, action) => {
      state.requestStatus[action.meta.arg] = "error";
    });
    builder.addCase(loadScanReadings.fulfilled, (state, action) => {
      const { cargoId, readings } = action.payload;
      state.requestStatus[cargoId] = "loaded";
      state.entities[cargoId] = readings;
    });
  },
});

export default slice.reducer;

export const { unloadScanReadings } = slice.actions;
